import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { DynamicBannerContent } from './useDynamicBanner.types';

interface DynamicBannerContextType {
  banner: DynamicBannerContent | null;
  showBanner: (banner: DynamicBannerContent) => void;
  hideBanner: () => void;
}

export const DynamicBannerContext = createContext<DynamicBannerContextType>({
  banner: null,
  showBanner: () => {},
  hideBanner: () => {},
});

/** `initialBanner`: shown from the first render, so a server render can carry it (PRNT-5494). */
export const useDynamicBannerState = (initialBanner?: DynamicBannerContent) => {
  const [banner, setBanner] = useState<DynamicBannerContent | null>(initialBanner ?? null);

  const showBanner = useCallback((banner: DynamicBannerContent) => {
    setBanner(banner);
  }, []);

  const hideBanner = useCallback(() => {
    setBanner(null);
  }, []);

  return useMemo(() => ({ banner, showBanner, hideBanner }), [banner, showBanner, hideBanner]);
};

export const useDynamicBanner = () => {
  return useContext(DynamicBannerContext);
};
